import { useEffect, useState } from 'react';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import { ImagePicker } from 'cat-ecommerce-alloy';
import { isConsentTypeAvailable } from '@app/utils';
import { ONETRUST, PAGE_BREAKPOINTS } from '@app/constants/commonConstants';
import ProductCarousel from '../../../../common/product/ProductCarousel';
import styles from './ProductGallery.module.scss';
import { useTranslation } from 'react-i18next';
import VideoWarning from '@app/components/common/VideoWarning/VideoWarning';
import { useBreakpoint } from '@app/hooks';
import ImageZoom from '@app/components/common/ImageZoom';
import OverlayMessage from '@app/components/common/OverlayMessage';
import PropTypes from 'prop-types';

const PRODUCT_FALLBACK_IMAGE =
  'https://caterpillar.scene7.com/is/image/Caterpillar/CM20170508-76204-57647';
const PRODUCT_IMAGE_SIZE_EXTRA_LARGE = '450px';
const PRODUCT_IMAGE_SIZE_LARGE = '400px';
const PRODUCT_IMAGE_SIZE_TABLET = '350px';
const THUMBNAIL_COUNT = 10;

const ProductGallery = ({ isDiscontinued }) => {
  const [t] = useTranslation();
  const PRODUCT_FALBACK_IMAGE_TITLE = t('CPG_PRODUCT_IMAGE');
  const ZOOM_THUMB = t('ZOOM_THUMB');

  const current = useSelector(s => s.products?.current);
  const galleryImages = useSelector(s => s.products?.byId?.[current]?.media);

  const [showWarning, setShowWarning] = useState(true);

  const cookiesEnableCheck = () => {
    if (isConsentTypeAvailable(ONETRUST.TARGET_COOKIES_ACCEPTED)) {
      setShowWarning(false);
    }
  };
  useEffect(() => {
    cookiesEnableCheck();
    window.addEventListener('OneTrustGroupsUpdated', () => {
      cookiesEnableCheck();
    });
  }, []);

  const isXL = useBreakpoint(PAGE_BREAKPOINTS.XL);
  const isLG = useBreakpoint(PAGE_BREAKPOINTS.LG);
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  let imageSize = PRODUCT_IMAGE_SIZE_TABLET;
  if (isXL) {
    imageSize = PRODUCT_IMAGE_SIZE_EXTRA_LARGE;
  } else if (isLG) {
    imageSize = PRODUCT_IMAGE_SIZE_LARGE;
  }

  return (
    <>
      {isMD && (
        <ImageZoom>
          <ImagePicker
            className={cx(
              '`w-100 d-flex justify-content-center mt-5 mb-5',
              styles['product-image-picker'],
              styles['product-icon-image-grid'],
              isDiscontinued && styles['image-opacity']
            )}
            defaultTitle={PRODUCT_FALBACK_IMAGE_TITLE}
            fallbackImage={PRODUCT_FALLBACK_IMAGE}
            imageHeight={imageSize}
            imageWidth={imageSize}
            images={galleryImages}
            videoWarningMessage={<VideoWarning />}
            showWarning={showWarning}
            thumbnailCount={THUMBNAIL_COUNT}
            thumbnailText={galleryImages?.length > 1 ? ZOOM_THUMB : null}
          />
          {/* If `isDiscontinued` is false, rendering an empty fragment instead of null/false,
              to avoid errors with React.cloneElement inside ImageZoom.
          */}
          {isDiscontinued ? (
            <OverlayMessage text={'DISCONTINUED_OVERLAY'} />
          ) : (
            <></>
          )}
        </ImageZoom>
      )}
      <ProductCarousel
        className={cx(
          'd-md-none',
          styles['product-icon-image-grid'],
          isDiscontinued && styles['image-opacity']
        )}
        images={galleryImages}
        videoWarningMessage={<VideoWarning />}
        showWarning={showWarning}
        isDiscontinued={isDiscontinued}
      />
    </>
  );
};

ProductGallery.propTypes = {
  isDiscontinued: PropTypes.bool
};

export default ProductGallery;
